"use client"
import { CENTRA_MARKET_CODE, type CentraMarketCode } from "@/data/supported-markets"
import logger from "@/lib/logger/logger-init"
import { GladlyScript } from "@/lib/scripts/Gladly/GladlyScript"
import { useCookieBot } from "@/src/hooks/useCookieBot"
import { getMarketCodeFromAppLocale } from "@/i18n/apps"
import { useHasUserEngaged } from "@/src/hooks/useHasUserEngaged"
import { usePathname } from "next/navigation"
import React, { useEffect, useState } from "react"

/*
 *   @TODO There are some issues with this implementation, mostly due to Gladly not being able to swap "app"
 *   So whenever one swap site, we actually reload the script and give it a new global property name.
 *   This is not optimal and should be fixed in the future since it creates a new script tag every time.
 */

// These are the available events that can be listened to
const GladlyAppEvents = [
    "availability:change",
    "conversation:ended",
    "conversation:started",
    "campaign:triggered",
    "customer:onboarded",
    "message:received",
    "message:sent",
    "quick-action:selected",
    "search:result-selected",
    "sidekick:closed",
    "sidekick:opened"
]

// These are the (currently) set up apps in Gladly
const gladlyApps = [
    "pocsports.com-APAC",
    "pocsports.com-EU",
    "pocsports.com-NorAm",
    "pocsports.com",
    "pocsports.com-test"
] as const

export type GladlyApps = (typeof gladlyApps)[number]

export type GladlyEnvironments = "PROD" | "STAGING"

// We do a config mapping for each environment and market code to use later
const gladlyConfiguredApps: Record<
    GladlyEnvironments,
    Record<CentraMarketCode | "default", GladlyApps>
> = {
    PROD: {
        [CENTRA_MARKET_CODE.Jp]: "pocsports.com-APAC",
        [CENTRA_MARKET_CODE.Eu]: "pocsports.com-EU",
        [CENTRA_MARKET_CODE.Na]: "pocsports.com-NorAm",
        [CENTRA_MARKET_CODE.Apac]: "pocsports.com-APAC",
        default: "pocsports.com"
    },
    STAGING: {
        [CENTRA_MARKET_CODE.Jp]: "pocsports.com",
        [CENTRA_MARKET_CODE.Eu]: "pocsports.com",
        [CENTRA_MARKET_CODE.Na]: "pocsports.com",
        [CENTRA_MARKET_CODE.Apac]: "pocsports.com",
        default: "pocsports.com"
    }
} as const

export type GladlyConfiguredApps = typeof gladlyConfiguredApps

export type GladlyProps = {
    params: { locale: string }
}

export const Gladly: React.FC<GladlyProps> = ({ params }) => {
    const FORCE_PRODUCTION_APPLICATIONS = false
    const [hasGladlyLoaded, setGladlyLoaded] = useState(false)
    const pathname = usePathname()
    const { consent } = useCookieBot()
    const hasInteracted = useHasUserEngaged()

    const environment: GladlyEnvironments =
        process.env.NEXT_PUBLIC_ENVIRONMENT === "production" ||
        FORCE_PRODUCTION_APPLICATIONS
            ? "PROD"
            : "STAGING"

    const appId =
        gladlyConfiguredApps[environment][
            getMarketCodeFromAppLocale(params.locale as string) ?? "default"
        ]

    const scriptName = `${environment}-${appId}`
    const currentGladlyWindowPropertyName = "Gladly-" + scriptName

    useEffect(() => {
        if (!window.poc) {
            window.poc = {}
        }
        if (!window.poc.gladly) {
            window.poc.gladly = {}
        }
    }, [])

    useEffect(() => {
        if (!hasGladlyLoaded || !window[currentGladlyWindowPropertyName]) {
            return
        }
        window[currentGladlyWindowPropertyName]?.navigate?.()
        window[currentGladlyWindowPropertyName]?.show?.()
        window[currentGladlyWindowPropertyName]?.close?.()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname])

    useEffect(() => {
        if (!hasGladlyLoaded || !window[currentGladlyWindowPropertyName]) {
            return
        }

        window[currentGladlyWindowPropertyName]
            .init({
                appId: appId
            })
            .then(() => {
                window["poc"]["gladly"][scriptName] =
                    window[currentGladlyWindowPropertyName]
                window[currentGladlyWindowPropertyName].show()
                window[currentGladlyWindowPropertyName].close()
            })
            .catch(function (error: string) {
                logger.error("Gladly init error:" + error, {
                    scriptName
                })
            })

        return () => {
            window[currentGladlyWindowPropertyName]?.close?.()
            delete window[currentGladlyWindowPropertyName]
            delete window.poc.gladly[scriptName]
            document.getElementById(`gladly-script-${appId}`)?.remove()
            document.getElementById(`gladlyChat_container`)?.remove()
            setGladlyLoaded(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasGladlyLoaded, appId, environment, scriptName])

    if (typeof window === "undefined" || !consent.necessary || !hasInteracted) return null

    return (
        <GladlyScript
            variableName={currentGladlyWindowPropertyName}
            environment={environment}
            appId={appId}
            readyCallback={() => setGladlyLoaded(true)}
            errorCallback={(error: string) => {
                logger.error("Gladly Script could not be loaded.", {
                    error
                })
            }}
        />
    )
}
